import {
  Card,
  CardHeader,
  CardBody,
  Row,
  Col,
  Label,
  Input,
  FormGroup,
  InputGroup,
  InputGroupText,
} from 'reactstrap';

import { useTranslation } from 'react-i18next';

import { Days, PromoFormValues } from 'types/promotion/promotionTypes';
import { FormikProps } from 'formik';
import Flatpickr from 'react-flatpickr';
import { Thai } from 'flatpickr/dist/l10n/th';
// import 'flatpickr/dist/themes/material_green.css';
import styles from 'pages/Promotions/styles/promo.module.css';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  PeriodTypesOptions,
  DaysOptions,
} from 'pages/Promotions/PromoConstant';
import { formatTo24HourMin } from 'utils/common/Time';
import { useSelector } from 'react-redux';
import { RootState } from 'slices';
import { FaCalendarAlt, FaClock } from 'react-icons/fa';

const fontColorClass = 'text-success';

const PromoPeriod = ({ formik }: { formik: FormikProps<PromoFormValues> }) => {
  const { t, i18n } = useTranslation();
  const getLocale = useCallback(() => {
    switch (i18n.language) {
      case 'th':
        return Thai;

      default:
        return undefined;
    }
  }, [i18n.language]);
  const flatpickrDateOptions = useCallback(
    () => ({
      dateFormat: 'd M, Y',
      ...(getLocale() ? { locale: getLocale() } : {}),
    }),
    [getLocale]
  );
  const promotionsState = useSelector((state: RootState) => state.Promotions);

  const startDateRef = useRef<any>(null);
  const endDateRef = useRef<any>(null);
  const startTimeRef = useRef<any>(null);
  const endTimeRef = useRef<any>(null);

  const { showAllValidateErrors } = promotionsState;

  const [isAllChecked, setIsAllChecked] = useState(false);
  const [isNoPeriod, setIsNoPeriod] = useState(true);

  const handleToggleAll = () => {
    const newCheckState = !isAllChecked;
    setIsAllChecked(newCheckState);

    DaysOptions(t).forEach((d) => {
      formik
        .setFieldValue(`promoPeriod.days.${d.key}`, newCheckState)
        .then(() => formik.setFieldTouched(`promoPeriod.days.${d.key}`, true));
    });
  };

  useEffect(() => {
    setIsNoPeriod(formik.values.notLimitDate === '1');
  }, [formik.values?.notLimitDate, setIsNoPeriod]);

  useEffect(() => {
    const allChecked = DaysOptions(t).every(
      (d) => formik.values.promoPeriod?.days?.[d.key as keyof Days]
    );
    setIsAllChecked(allChecked);
  }, [formik.values?.promoPeriod?.days, t]);

  return (
    <Card>
      <CardHeader>
        <h5 className="card-title mb-0">{t('Promo_Period')}</h5>
      </CardHeader>
      <CardBody className={`${styles['card-body-pd']}`}>
        <Row>
          <Col lg={6}>
            <div className="mb-3 row">
              <Label className="col-sm-3 col-form-label"></Label>
              <Col sm={9}>
                <div className="mt-2">
                  <FormGroup key={3}>
                    {PeriodTypesOptions(t).map(
                      (p: { label: string; value: string }, _index) => (
                        <div
                          key={p.value}
                          className="form-check form-check-inline"
                        >
                          <Input
                            className="form-check-input"
                            type="radio"
                            name="notLimitDate"
                            id={p.label}
                            value={p.value}
                            checked={formik.values.notLimitDate === p.value}
                            onChange={(e) => {
                              formik.handleChange(e);
                              if (e.target.value === '1') {
                                setIsNoPeriod(true);
                              } else {
                                setIsNoPeriod(false);
                              }
                              // handleChangeProductType(e, formik, dispatch);
                            }}
                          />
                          <Label className="form-check-label" htmlFor={p.label}>
                            {p.label}
                          </Label>
                        </div>
                      )
                    )}
                  </FormGroup>
                </div>
              </Col>
            </div>

            <div
              className={`mb-3 row ${isNoPeriod ? 'd-none' : ''}`}
              id="promo-period"
            >
              <Label
                htmlFor="promoPeriod.dateRange"
                className="col-sm-3 col-form-label"
              >
                {t('Active_Date_Range')} <span style={{ color: 'red' }}>*</span>
              </Label>
              <Col xs={10} sm={4}>
                <InputGroup className="mb-2">
                  <Flatpickr
                    ref={startDateRef}
                    id="promoPeriod.dateRange.start"
                    name="promoPeriod.dateRange.start"
                    className="form-control"
                    options={flatpickrDateOptions()}
                    value={formik.values.promoPeriod?.dateRange?.start || ''}
                    onChange={(date) =>
                      formik
                        .setFieldValue('promoPeriod.dateRange.start', date[0])
                        .then(() =>
                          formik.setFieldTouched(
                            'promoPeriod.dateRange.start',
                            true
                          )
                        )
                    }
                    onClick={() => {
                      formik.setFieldTouched(
                        'promoPeriod.dateRange.start',
                        true
                      );
                    }}
                  />
                  <InputGroupText
                    onClick={() => startDateRef.current?.flatpickr?.open()}
                    style={{ cursor: 'pointer' }}
                  >
                    <FaCalendarAlt className={`${fontColorClass}`} />
                  </InputGroupText>
                </InputGroup>
                {(formik.touched.promoPeriod?.dateRange?.start &&
                  formik.errors.promoPeriod?.dateRange?.start &&
                  typeof formik.errors.promoPeriod.dateRange.start ===
                    'string') ||
                (formik.isValid === false &&
                  formik.errors.promoPeriod?.dateRange?.start &&
                  showAllValidateErrors === true) ? (
                  <div className="text-danger mt-1">
                    {formik.errors.promoPeriod.dateRange.start}
                  </div>
                ) : null}
              </Col>
              <Col xs={1} sm={1}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  {t('to')}
                </div>
              </Col>
              <Col xs={10} sm={4}>
                <InputGroup className="mb-2">
                  <Flatpickr
                    ref={endDateRef}
                    id="promoPeriod.dateRange.end"
                    name="promoPeriod.dateRange.end"
                    className="form-control"
                    options={flatpickrDateOptions()}
                    value={formik.values.promoPeriod?.dateRange?.end || ''}
                    onChange={(date) =>
                      formik
                        .setFieldValue('promoPeriod.dateRange.end', date[0])
                        .then(() =>
                          formik.setFieldTouched(
                            'promoPeriod.dateRange.end',
                            true
                          )
                        )
                    }
                    onClick={() => {
                      formik.setFieldTouched('promoPeriod.dateRange.end', true);
                    }}
                  />
                  <InputGroupText
                    onClick={() => endDateRef.current?.flatpickr?.open()}
                    style={{ cursor: 'pointer' }}
                  >
                    <FaCalendarAlt className={`${fontColorClass}`} />
                  </InputGroupText>
                </InputGroup>
                {(formik.touched.promoPeriod?.dateRange?.end &&
                  formik.errors.promoPeriod?.dateRange?.end &&
                  typeof formik.errors.promoPeriod.dateRange.end ===
                    'string') ||
                (formik.isValid === false &&
                  formik.errors.promoPeriod?.dateRange?.end &&
                  showAllValidateErrors === true) ? (
                  <div className="text-danger mt-1">
                    {formik.errors.promoPeriod.dateRange.end}
                  </div>
                ) : null}
              </Col>
            </div>

            <div className={`mb-3 row ${isNoPeriod ? 'd-none' : ''}`}>
              <Label
                htmlFor="promoPeriod.timeRange"
                className="col-sm-3 col-form-label"
              >
                {t('Active_Time_Range')} <span style={{ color: 'red' }}>*</span>
              </Label>
              <Col xs={5} sm={4}>
                <InputGroup className="mb-2">
                  <Flatpickr
                    ref={startTimeRef}
                    id="promoPeriod.timeRange.start"
                    name="promoPeriod.timeRange.start"
                    className="form-control"
                    options={{
                      enableTime: true,
                      noCalendar: true,
                      dateFormat: 'H:i',
                      time_24hr: true,
                    }}
                    value={formik.values.promoPeriod?.timeRange?.start || ''}
                    onChange={(date) => {
                      formik.setFieldValue(
                        'promoPeriod.timeRange.start',
                        formatTo24HourMin(date[0])
                      );
                    }}
                    // onChange={formik.handleChange}
                    onClick={() => {
                      formik.setFieldTouched(
                        'promoPeriod.timeRange.start',
                        true
                      );
                    }}
                    onBlur={() => {
                      formik.setFieldTouched(
                        'promoPeriod.timeRange.start',
                        true
                      );
                    }}
                  />
                  <InputGroupText
                    onClick={() => startTimeRef.current?.flatpickr?.open()}
                    style={{ cursor: 'pointer' }}
                  >
                    <FaClock className={`${fontColorClass}`} />
                  </InputGroupText>
                </InputGroup>
                {(formik.touched.promoPeriod?.timeRange?.start &&
                  formik.errors.promoPeriod?.timeRange?.start &&
                  typeof formik.errors.promoPeriod.timeRange.start ===
                    'string') ||
                (formik.isValid === false &&
                  formik.errors.promoPeriod?.timeRange?.start &&
                  showAllValidateErrors === true) ? (
                  <div className="text-danger mt-1">
                    {formik.errors.promoPeriod.timeRange.start}
                  </div>
                ) : null}
              </Col>
              <Col xs={1} sm={1}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  {t('to')}
                </div>
              </Col>
              <Col xs={5} sm={4}>
                <InputGroup className="mb-2">
                  <Flatpickr
                    ref={endTimeRef}
                    id="promoPeriod.timeRange.end"
                    name="promoPeriod.timeRange.end"
                    className="form-control"
                    options={{
                      enableTime: true,
                      noCalendar: true,
                      dateFormat: 'H:i',
                      time_24hr: true,
                    }}
                    value={formik.values.promoPeriod?.timeRange?.end || ''}
                    onChange={(date) =>
                      formik.setFieldValue(
                        'promoPeriod.timeRange.end',
                        formatTo24HourMin(date[0])
                      )
                    }
                    onClick={() => {
                      formik.setFieldTouched('promoPeriod.timeRange.end', true);
                    }}
                    onBlur={() => {
                      formik.setFieldTouched('promoPeriod.timeRange.end', true);
                    }}
                  />
                  <InputGroupText
                    onClick={() => endTimeRef.current?.flatpickr?.open()}
                    style={{ cursor: 'pointer' }}
                  >
                    <FaClock className={`${fontColorClass}`} />
                  </InputGroupText>
                </InputGroup>
                {(formik.touched.promoPeriod?.timeRange?.end &&
                  formik.errors.promoPeriod?.timeRange?.end &&
                  typeof formik.errors.promoPeriod.timeRange.end ===
                    'string') ||
                (formik.isValid === false &&
                  formik.errors.promoPeriod?.timeRange?.end &&
                  showAllValidateErrors === true) ? (
                  <div className="text-danger mt-1">
                    {formik.errors.promoPeriod.timeRange.end}
                  </div>
                ) : null}
              </Col>
            </div>

            <div className={`mb-3 row ${isNoPeriod ? 'd-none' : ''}`}>
              <Label
                htmlFor="promoPeriod.days"
                className="col-sm-3 col-form-label"
              >
                {t('Specify_active_days')}{' '}
                <span style={{ color: 'red' }}>*</span>
              </Label>
              <Col sm={9}>
                <div className="form-check form-check-success mb-3">
                  <Input
                    className="form-check-input"
                    type="checkbox"
                    id="toggleAllDays"
                    checked={isAllChecked}
                    onChange={handleToggleAll}
                  />
                  <Label className="form-check-label" htmlFor="toggleAllDays">
                    {t('Check_All')}
                  </Label>
                </div>
                <div className="d-flex flex-wrap">
                  {DaysOptions(t).map(
                    (d: { label: string; key: string }, index) => (
                      <div
                        key={index}
                        className="form-check form-check-success mb-2 me-2"
                      >
                        <Input
                          className="form-check-input"
                          type="checkbox"
                          id={`promoPeriod.days.${d.key}`}
                          name={`promoPeriod.days.${d.key}`}
                          // onChange={formik.handleChange}
                          onChange={(e) => {
                            formik
                              .setFieldValue(
                                `promoPeriod.days.${d.key}`,
                                e.target.checked
                              )
                              .then(() =>
                                formik.setFieldTouched(
                                  `promoPeriod.days.${d.key}`,
                                  true
                                )
                              );
                          }}
                          checked={
                            formik.values.promoPeriod?.days?.[
                              d.key as keyof Days
                            ] || false
                          }
                        />
                        <Label
                          className="form-check-label"
                          htmlFor={`promoPeriod.days.${d.key}`}
                        >
                          {t(d.label)}
                        </Label>
                      </div>
                    )
                  )}
                </div>

                {(formik.touched.promoPeriod?.days &&
                  formik.errors.promoPeriod?.days &&
                  typeof formik.errors.promoPeriod.days === 'string') ||
                (formik.isValid === false &&
                  formik.errors.promoPeriod?.days &&
                  showAllValidateErrors === true) ? (
                  <div className="text-danger mt-1">
                    {formik.errors.promoPeriod.days as string}
                  </div>
                ) : null}
              </Col>
            </div>
          </Col>

          <Col lg={6}></Col>
        </Row>
      </CardBody>
    </Card>
  );
};
export default PromoPeriod;
